import { create } from 'zustand';
import { doDELETE, doGET, doPOST, doPUT } from '../../utils/HttpUtils';
import { showError, showSuccess } from '../toaster';
import { HR, HRFilters, HRState } from '../../types/hr';

const BASE_URL = '/hr';

const store = create<HRState>((set, get) => ({
  data: [],
  totalPages: 0,
  currentPage: 1,
  filters: {},
  isLoading: false,
  rows: 20,
  total: 0,

  fetchGrid: async () => {
    try {
      const { filters, currentPage, rows, isLoading } = get();
      if (isLoading) return;

      set({ isLoading: true });

      const queryParams = new URLSearchParams(filters as Record<string, string>);
      queryParams.append('page', String(currentPage));
      queryParams.append('limit', String(rows));
      const response = await doGET(`${BASE_URL}?${queryParams.toString()}`);

      if (response.status >= 200 && response.status < 400) {
        const hrRows = Array.isArray(response.data?.data) ? response.data.data : [];
        const pagination = response.data?.pagination;
        set({
          data: hrRows,
          total: pagination?.total ?? hrRows.length,
          totalPages: pagination?.totalPages ?? 1,
          currentPage: pagination?.page ?? currentPage,
        });
      } else {
        showError(response.message || 'Failed to fetch HR records');
      }
    } catch (_error) {
      showError('Failed to fetch HR records');
    } finally {
      set({ isLoading: false });
    }
  },

  setFilters: (newFilters: HRFilters) => {
    set({ filters: newFilters, currentPage: 1 });
    get().fetchGrid();
  },

  nextPage: () => {
    const { currentPage, totalPages, fetchGrid } = get();
    if (currentPage < totalPages) {
      set((state) => ({ currentPage: state.currentPage + 1 }));
      fetchGrid();
    }
  },

  prevPage: () => {
    const { currentPage, fetchGrid } = get();
    if (currentPage > 1) {
      set((state) => ({ currentPage: state.currentPage - 1 }));
      fetchGrid();
    }
  },

  onPageChange: (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    const { totalPages } = get();
    const newPage = page + 1;
    if (newPage >= 1 && newPage <= totalPages) {
      set({ currentPage: newPage });
      get().fetchGrid();
    }
  },

  onCreate: async (data: HR) => {
    try {
      const response = await doPOST(BASE_URL, data);
      if (response.status >= 200 && response.status < 400) {
        get().fetchGrid();
        showSuccess('HR created successfully');
        return response.data?.data;
      }
      showError(response.message || 'Failed to create HR');
      return null;
    } catch (_error) {
      showError('Failed to create HR');
      return null;
    }
  },

  onUpdate: async (data: HR) => {
    try {
      if (!data._id) {
        showError('HR id is required for update');
        return null;
      }

      const response = await doPUT(`${BASE_URL}/${data._id}`, data);
      if (response.status >= 200 && response.status < 400) {
        get().fetchGrid();
        showSuccess('HR updated successfully');
        return response.data?.data;
      }
      showError(response.message || 'Failed to update HR');
      return null;
    } catch (_error) {
      showError('Failed to update HR');
      return null;
    }
  },

  onDelete: async (id: string) => {
    try {
      const response = await doDELETE(`${BASE_URL}/${id}`);
      if (response.status >= 200 && response.status < 400) {
        get().fetchGrid();
        showSuccess('HR deleted successfully');
      } else {
        showError(response.message || 'Failed to delete HR');
      }
    } catch (_error) {
      showError('Failed to delete HR');
    }
  },

  detail: async (id: string) => {
    try {
      const response = await doGET(`${BASE_URL}/${id}`);
      if (response.status >= 200 && response.status < 400) {
        return response.data?.data || null;
      }
      showError(response.message || 'Failed to fetch HR details');
      return null;
    } catch (_error) {
      showError('Failed to fetch HR details');
      return null;
    }
  },
}));

export const useHRStore = () => store((state) => state);
